import NewLeagueListingItem from "components/league/NewListingItem";
import LeagueInterface from "interfaces/league";
import moment from "moment";
import React, {ReactNode, useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import {useRecoilState} from "recoil";
import {
    TETabs,
    TETabsContent,
    TETabsItem,
    TETabsPane,
} from "tw-elements-react";
import MiniGameListingItem from "../../components/league/MiniGameListingItem";
import * as SBService from "../../helpers/sportbookService";
import {CONSTANT} from "../../shared/constant";
import {
    bottomBarState,
    loaderState,
    showBottomBarState,
    userState,
} from "../../states/common";
import Icon from "../../components/icon";
import {Sheet} from 'react-modal-sheet';
import {LeagueTypeFiltering} from "../../shared/enums/LeagueTypeEnum";
import * as _ from "lodash";
import {TempFilterState} from "../../interfaces/leagueFilter";
import FilteringWithSearch from "../../components/league/FilteringWithSearch";
import FilteringWithSearchWithSub from "../../components/league/FilteringWithSearchWithSub";
import {LeagueFilterStatusEnum} from "../../shared/enums/LeagueFilterStatusEnum";
import {useNavigate, useSearchParams} from "react-router-dom";
import TIcon from "../../components/ui/TIcon";
import {soGiaiDangThamGia} from "./badgeDangThamGia";

/**
 * Bốn tab, xếp theo việc người dùng làm được: đang tham gia → đăng ký được →
 * chờ mở đăng ký → đã xong.
 *
 * Hai tab giữa bám mốc ngày ĐĂNG KÝ chứ không phải ngày thi đấu. "Đã kết thúc"
 * cũng chỉ liệt kê giải của chính người dùng — lịch sử thi đấu của họ, không
 * phải kho lưu trữ mọi giải từng chạy.
 *
 * Khoá ở đây trùng với ?tab=... mà trang chủ và trang cá nhân dẫn sang.
 */
const TABS = [
    {key: 'joined', label: 'ĐANG THAM GIA', filter: LeagueFilterStatusEnum.NOT_ENDED, onlyMyLeagues: true},
    {key: 'open', label: 'ĐANG MỞ ĐĂNG KÝ', filter: LeagueFilterStatusEnum.REGISTRATION_OPEN, onlyMyLeagues: false},
    {key: 'upcoming', label: 'SẮP TỚI', filter: LeagueFilterStatusEnum.REGISTRATION_UPCOMING, onlyMyLeagues: false},
    {key: 'ended', label: 'ĐÃ KẾT THÚC', filter: LeagueFilterStatusEnum.ENDED, onlyMyLeagues: true},
];

const tabByKey = (key: string | null) => TABS.find((t) => t.key === key) ?? null;

const EMPTY_TEXT: Record<string, { title: string; hint: string }> = {
    joined: {title: 'Bạn chưa tham gia giải nào', hint: 'Đăng ký một giải ở tab Đang mở đăng ký'},
    open: {title: 'Chưa có giải nào đang mở đăng ký', hint: 'Xem tab Sắp tới để biết giải nào sắp mở'},
    upcoming: {title: 'Chưa có giải nào sắp mở đăng ký', hint: 'Quay lại sau, ban tổ chức sẽ công bố lịch'},
    ended: {title: 'Bạn chưa có giải nào đã kết thúc', hint: 'Giải bạn tham gia sẽ chuyển về đây khi kết thúc'},
};

const LeagueOnUpListingPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loader, setLoader] = useRecoilState(loaderState);
    const [, setActive] = useRecoilState(bottomBarState);
    const [bottomBar, setBottomBar] = useRecoilState(showBottomBarState);
    const [leagues, setLeagues] = useState<LeagueInterface[]>([]);
    const [miniGames, setMiniGames] = useState([]);
    const [maxPage, setMaxPage] = useState(1);
    const [isOpenAddressSheet, setIsOpenAddressSheet] = useState(false);
    const [isOpenVenueSheet, setIsOpenVenueSheet] = useState(false);
    const [isOpenLeagueTypeSheet, setIsOpenLeagueTypeSheet] = useState(false);
    const [listVenues, setListVenues] = useState<[]>([]);
    const [listProvince, setListProvince] = useState<[]>([]);

    const [user,] = useRecoilState(userState);

    /* Ước lượng từ hồ sơ: đã đăng ký và chưa qua ngày kết thúc. Dùng khi chưa
       có danh sách thật để đếm — nó KHÔNG biết luật "đã có vô địch thì coi như
       đã kết thúc" của API, nên có thể đếm dôi vài giải Swiss/loại trực tiếp đã
       đấu xong chung kết trước ngày kết thúc dự kiến. */
    const joinedCount = useMemo(
        () => (user.leagues ?? []).filter((l: any) => moment(l.endDate).endOf('day').isAfter(moment())).length,
        [user]
    );

    /* Danh sách của tab hiện tại đã tải HẾT chưa (không còn trang sau). Chỉ khi
       đó số lượng đang hiển thị mới là con số đầy đủ để đem lên badge. */
    const [listComplete, setListComplete] = useState(false);

    /* Tab mở sẵn. Layout chỉ vẽ trang khi hồ sơ đã tải xong (hoặc khi là khách),
       nên tại đây user.leagues đã đáng tin — quyết định được ngay, không cần
       chờ rồi nhảy tab trước mắt người dùng. */
    const initialTab = () => {
        const requested = tabByKey(searchParams.get('tab'));
        if (requested) return requested.key;
        return joinedCount > 0 ? 'joined' : 'open';
    };
    const [tab, setTab] = useState<string>(initialTab);
    const activeTab = tabByKey(tab) ?? TABS[1];

    /* Tự chuyển "đang mở đăng ký" → "sắp tới" đúng một lần, khi tab đó không có
       giải nào và người dùng chưa tự chọn tab. Không có nó thì người mới mở app
       gặp ngay một danh sách rỗng dù hệ thống vẫn có giải sắp mở. */
    /* Vào thẳng bằng ?tab= thì coi như người dùng đã chọn — đừng nhảy tab dưới
       chân họ. */
    const autoAdvanced = useRef(!!tabByKey(searchParams.get('tab')));

    const [filter, setFilter] = useState<TempFilterState>({
        firstLoad: true,
        page: 1,
        limit: CONSTANT.limit,
        leagueType: '',
        provinceCode: '',
        districtCode: [],
        venues: [],
        filter: activeTab.filter,
        onlyMyLeagues: activeTab.onlyMyLeagues
    });

    const [currentTab, setCurrentTab] = useState("league");

    useEffect(() => {
        const currentFilterStorage = localStorage.getItem("oldFilterLeague");
        if (currentFilterStorage) {
            let cachedFilterObject = JSON.parse(currentFilterStorage);
            /* Tab không được dính lại từ lần trước — nó do ?tab= hoặc do người
               dùng có giải hay không quyết định, không phải bộ lọc đã lưu. */
            delete cachedFilterObject.filter;
            delete cachedFilterObject.onlyMyLeagues;
            const tmpFilter = {...filter, ...cachedFilterObject, firstLoad: false};
            setFilter(tmpFilter);
        } else {
            setFilter({...filter, firstLoad: false});
        }
        document.title = "Biadi — Giải đấu";
    setActive("league");
        setBottomBar(true);

        loadAllVenue('', [])
        loadAllProvince()
    }, []);

    const listJoinedId = useMemo(
        () => (user.leagues ? user.leagues.map((league) => league.id) : ""),
        [user]
    );

    const loadAllLeague = async (tab = "league") => {
        /* Khách chưa đăng nhập mà mở tab "Đã đăng ký": không gọi API. Bản công
           khai của /leagues không biết "của tôi" là ai nên nó trả về mọi giải —
           hiện chúng dưới tiêu đề "GIẢI ĐÃ ĐĂNG KÝ" là nói dối người dùng. */
        if (filter.onlyMyLeagues && !user.id) {
            setLeagues([]);
            setListComplete(true);
            return;
        }
        if ((filter.page === 1 && leagues.length === 0) || filter.page < maxPage) {
            setLoader(true);
            try {
                let data;
                if (tab === "minigame") {
                    data = await SBService.getMiniGame(filter);
                } else {
                    data = await SBService.getLeagues(filter);
                }

                /* Còn trang sau thì số đang hiển thị chưa phải con số cuối —
                   badge phải quay về ước lượng từ hồ sơ. */
                setListComplete(!data?.hasNextPage);

                if (data && data.data.length > 0) {
                    if (data.hasNextPage) {
                        setMaxPage(maxPage + 1);
                        setFilter({...filter, page: data.page});
                    } else {
                        setMaxPage(1);
                    }
                    if (tab === "minigame") {
                        setMiniGames(miniGames.concat(data.data));
                    } else {
                        setLeagues(leagues.concat(data.data));
                    }
                }
            } catch (e: any) {
                setListComplete(false);
                if (e.response && e.response.data && e.response.data.error) {
                    toast("Có lỗi xảy ra: " + e.response.data.error);
                } else {
                    toast("Có lỗi xảy ra: " + e.message);
                }
            }
            setLoader(false);
        }
    };

    const loadAllProvince = async () => {
        const data = await SBService.loadProvince();
        if (data.data.length > 0) {
            const provinceData = data.data.map((province: any) => ({value: province.code, label: province.name}));
            setListProvince(provinceData);
        }

    }

    const loadAllVenue = async (provinceCode: string, districtCode: Array<String>) => {
        const data = await SBService.loadVenueData(provinceCode, districtCode);
        if (data.data) {
            const venueData = data.data.map((venue: any) => ({value: venue.id, label: venue.name}));
            setListVenues(venueData)
        }
    }

    const loadDistrictOfProvince = async (provinceCode: string) => {
        const data = await SBService.loadDistrictProvince(provinceCode);
    }

    const handleBasicClick = (tab: string) => {
        setCurrentTab(tab);
        loadAllLeague(tab);
    };


    const applyFiltering = (type: string, value: any) => {
        let tmp: any = _.cloneDeep(filter)
        if (_.isObject(value) && !_.isArray(value)) {
            tmp = {...tmp, ...value};
        } else {
            tmp[type] = value;
        }
        const filteringData = {...tmp, page: 1}
        setLeagues([]);
        setListComplete(false);
        setFilter(filteringData);
        localStorage.setItem('oldFilterLeague', JSON.stringify(filteringData));
        loadAllLeague(currentTab)
        switch (type) {
            case 'leagueType':
                setIsOpenLeagueTypeSheet(false)
                break;
            case 'provinceCode':
                setIsOpenAddressSheet(false)
                break;
            case 'venues':
                setIsOpenVenueSheet(false)
                break;
        }
    }

    useEffect(() => {
        if (!filter.firstLoad) {
            loadAllLeague(currentTab)
        }
    }, [filter]);

    /* Đổi tab phải xoá danh sách cũ vì loadAllLeague nối thêm chứ không thay thế. */
    const changeStatus = (key: string, manual = true) => {
        if (key === tab) return;
        const next = tabByKey(key);
        if (!next) return;
        if (manual) autoAdvanced.current = true;
        setLeagues([]);
        setListComplete(false);
        setMiniGames([]);
        setMaxPage(1);
        setTab(key);
        setFilter({...filter, filter: next.filter, onlyMyLeagues: next.onlyMyLeagues, page: 1});
    };

    const TYPE_PILLS = [
        {value: '', label: 'Tất cả'},
        ...LeagueTypeFiltering.map((t) => ({value: t.value as string, label: t.label})),
    ];

    const changeType = (value: string) => {
        if (value === filter.leagueType) return;
        setLeagues([]);
        setListComplete(false);
        setMaxPage(1);
        setFilter({...filter, leagueType: value, page: 1});
    };

    const dropdowns = [
        {label: 'Khu vực', count: filter.provinceCode ? 1 : 0, onClick: () => setIsOpenAddressSheet(true)},
        {label: 'Câu lạc bộ', count: filter.venues.length, onClick: () => setIsOpenVenueSheet(true)},
    ];

    const sectionTitle = activeTab.label;

    /* Luật đếm nằm ở soGiaiDangThamGia (badgeDangThamGia.ts). */
    const khongLoc =
        !filter.leagueType && !filter.provinceCode &&
        filter.districtCode.length === 0 && filter.venues.length === 0;
    const joinedBadge = soGiaiDangThamGia({
        tab, listComplete, khongLoc, soDangHien: leagues.length, soTheoHoSo: joinedCount,
    });

    /* Chỉ chạy sau khi tab "đang mở đăng ký" đã tải xong mà không có giải nào,
       và chỉ khi người dùng chưa tự chọn tab. */
    useEffect(() => {
        if (autoAdvanced.current || loader) return;
        if (tab !== 'open' || leagues.length > 0 || filter.firstLoad) return;
        autoAdvanced.current = true;
        changeStatus('upcoming', false);
    }, [tab, leagues.length, loader, filter.firstLoad]);

    return (
        <>
            <header className="page-head">
                <h1>GIẢI ĐẤU</h1>
                <button type="button" className="icon-btn" aria-label="Tìm kiếm"
                        onClick={() => setIsOpenVenueSheet(true)}>
                    <TIcon name="search"/>
                </button>
            </header>

            {/* Lọc theo trạng thái giải — nối thẳng vào tham số `filter` của API */}
            <div className="seg seg--scroll">
                {TABS.map((o) => (
                    <button key={o.key} type="button"
                            className={`seg-item${tab === o.key ? ' active' : ''}`}
                            onClick={() => changeStatus(o.key)}>
                        {o.label}
                        {o.key === 'joined' && joinedBadge > 0 ? (
                            <span className="seg-badge">{joinedBadge}</span>
                        ) : null}
                    </button>
                ))}
            </div>

            {/* Lọc theo thể thức */}
            <div className="chips" style={{paddingTop: 14}}>
                {TYPE_PILLS.map((o) => (
                    <button key={o.value || 'all'} type="button"
                            className={`chip${filter.leagueType === o.value ? ' active' : ''}`}
                            onClick={() => changeType(o.value)}>
                        {o.label}
                    </button>
                ))}
            </div>

            {/* Lọc theo khu vực / câu lạc bộ */}
            <div style={{display: 'flex', gap: 10, paddingTop: 12}}>
                {dropdowns.map((d) => (
                    <button key={d.label} type="button" onClick={d.onClick}
                            style={{
                                flex: 1, minWidth: 0, display: 'flex', alignItems: 'center',
                                justifyContent: 'space-between', gap: 8, padding: '0 14px',
                                height: 42, borderRadius: 10,
                                border: '1px solid var(--biadi-line)',
                                background: 'var(--biadi-navy-soft)',
                                color: 'var(--biadi-steel)',
                                font: '500 12px var(--font-ui)',
                            }}>
                        <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {d.label}
                            {d.count ? <span style={{color: 'var(--biadi-red)'}}> ({d.count})</span> : null}
                        </span>
                        <TIcon name="chevron-down"/>
                    </button>
                ))}
            </div>

            <section className="section">
                <div className="section-head"><h2>{sectionTitle}</h2></div>

                {leagues.length > 0 ? (
                    leagues.map((league: any, index) => (
                        <NewLeagueListingItem
                            key={`league-listing-item-${index}`}
                            isShowScanBtn={false}
                            isJoined={listJoinedId.includes(league.id)}
                            league={league}
                        />
                    ))
                ) : !loader ? (
                    /* Khách chưa đăng nhập thì "chưa đăng ký giải nào" là câu sai —
                       không phải họ chưa đăng ký, mà là chưa có tài khoản để đọc
                       danh sách. Đưa thẳng nút đăng nhập. */
                    activeTab.onlyMyLeagues && !user.id ? (
                        <div className="ui-card empty-state">
                            <TIcon name="trophy-off"/>
                            <div className="t">Đăng nhập để xem giải của bạn</div>
                            <div className="h">Danh sách này gắn với tài khoản của bạn</div>
                            <button type="button" className="btn btn-primary"
                                    style={{marginTop: 14}}
                                    onClick={() => navigate('/auth/login')}>
                                ĐĂNG NHẬP
                            </button>
                        </div>
                    ) : (
                        <div className="ui-card empty-state">
                            <TIcon name="trophy-off"/>
                            <div className="t">{EMPTY_TEXT[tab]?.title ?? 'Không có giải nào khớp bộ lọc'}</div>
                            <div className="h">{EMPTY_TEXT[tab]?.hint ?? 'Thử đổi tab hoặc bỏ bớt điều kiện lọc'}</div>
                        </div>
                    )
                ) : null}

                {currentTab === 'minigame' && miniGames.length > 0 ? (
                    miniGames.map((league: any, index) => (
                        <MiniGameListingItem
                            key={`minigame-listing-item-${index}`}
                            isShowScanBtn={false}
                            isJoined={listJoinedId.includes(league.id)}
                            league={league}
                        />
                    ))
                ) : null}
            </section>

            <Sheet snapPoints={[0.67]} initialSnap={0} isOpen={isOpenLeagueTypeSheet} onClose={() => {
                setIsOpenLeagueTypeSheet(false);
            }}>
                <Sheet.Container>
                    <Sheet.Header>
                        <h3 className="text-2xl uppercase p-4 w-full text-center">Loại giải</h3>
                    </Sheet.Header>
                    <Sheet.Content>
                        <div className="filter-form w-full bg-[#1B2436] rounded px-3">
                            <FilteringWithSearch items={LeagueTypeFiltering} currentFiltering={filter.leagueType}
                                                 isEquals={true} onChangeHandler={applyFiltering}
                                                 filterType="leagueType"/>
                        </div>
                    </Sheet.Content>
                </Sheet.Container>
                <Sheet.Backdrop onTap={() => {
                    setIsOpenLeagueTypeSheet(false);
                }}/>
            </Sheet>

                <Sheet snapPoints={[0.67]} initialSnap={0} isOpen={isOpenVenueSheet} onClose={() => {
                setIsOpenVenueSheet(false);
            }}>
                <Sheet.Container>
                    <Sheet.Header>
                        <h3 className="text-2xl uppercase p-4 w-full text-center">Câu lạc bộ</h3>
                    </Sheet.Header>
                    <Sheet.Content>
                        <div className="filter-form w-full bg-[#1B2436] rounded px-3">
                            <FilteringWithSearch items={listVenues} currentFiltering={filter.venues} isEquals={false}
                                                 onChangeHandler={applyFiltering} filterType="venues"/>
                        </div>
                    </Sheet.Content>
                </Sheet.Container>
                <Sheet.Backdrop onTap={() => {
                    setIsOpenVenueSheet(false);
                }}/>
            </Sheet>

           <Sheet snapPoints={[0.67]} initialSnap={0} isOpen={isOpenAddressSheet} onClose={() => {
                setIsOpenAddressSheet(false);
            }}>
                <Sheet.Container>
                    <Sheet.Header>
                        <h3 className="text-2xl uppercase p-4 w-full text-center">Khu vực</h3>
                    </Sheet.Header>
                    <Sheet.Content>
                        <div className="filter-form w-full bg-[#1B2436] rounded px-3 h-[100%] overflow-auto">
                            <FilteringWithSearchWithSub items={listProvince} currentFiltering={filter.provinceCode}
                                                 isEquals={true} onChangeHandler={applyFiltering}
                                                 filterType="provinceCode" isHasSubFilter={true}
                                                 subCurrentFiltering={filter.districtCode}
                                                 subFilteringType={'districtCode'}/>
                        </div>
                    </Sheet.Content>
                </Sheet.Container>
                <Sheet.Backdrop onTap={() => {
                    setIsOpenAddressSheet(false);
                }}/>
            </Sheet>
        </>
    );
};

export default LeagueOnUpListingPage;
